"use client";
import { SUPCard } from "@/components/sup/card";
import { type SubUnit } from "@/types/sup";
import { s3Url } from "@/utils/s3-prefix";
import * as React from "react";

interface SubUnitsSectionProps {
	subUnits: SubUnit[];
}

// Helper function to group subunits by floor
const groupSubUnitsByFloor = (subUnits: SubUnit[]) => {
	const grouped = subUnits.reduce(
		(acc, unit) => {
			const floor = unit.floor;
			if (!acc[floor]) {
				acc[floor] = [];
			}
			acc[floor].push(unit);
			return acc;
		},
		{} as Record<number, SubUnit[]>,
	);

	// Sort floors in ascending order
	return Object.entries(grouped)
		.sort(([a], [b]) => parseInt(a) - parseInt(b))
		.reduce(
			(acc, [floor, units]) => {
				acc[parseInt(floor)] = units;
				return acc;
			},
			{} as Record<number, SubUnit[]>,
		);
};

// Helper function to convert SubUnit to SellerUnitAndProject format for SUPCard
const convertSubUnitToCardData = (subUnit: SubUnit) => {
	const floorplan = subUnit.floorplan
		? {
				space: "floorplan",
				name: "Floorplan",
				url: subUnit.floorplan ? s3Url(`units/${subUnit.unitId}/floorplans/${subUnit.floorplan}`) : "",
				type: "image",
			}
		: null;
	const imageList =
		subUnit.images && subUnit.images.length > 0
			? subUnit.images
					.map(image => ({
						space: image.space,
						name: image.name,
						url: image.original ? s3Url(image.original) : "",
						type: "image",
					}))
					.filter(image => image.url !== floorplan?.url)
			: [];
	return {
		_id: subUnit.unitId,
		bedrooms: [subUnit.bedrooms],
		bathrooms: [subUnit.bathrooms],
		facings: [subUnit.facing],
		propertyTypes: [subUnit.propertyType],
		prices: [subUnit.rentalValue],
		builtUpAreas: [],
		parkings: [], // Not available in SubUnit
		amenities: [], // Not available in SubUnit
		// mediaList: images.filter(image => image.url !== ""),
		mediaList: imageList,
		cover: floorplan ? floorplan.url : "",
		appCoverImage: floorplan ? floorplan.url : "",
		projectId: "",
		projectName: "",
		localityName: "",
		type: "unit" as const,
		updatedAt: new Date(),
		distance: 0,
		// Add floor information for display
		floor: subUnit.floor,
		// Add additional properties that might be needed
		possessionStatuses: [],
		currentRate: 0,
		owners: [],
		balconies: [],
		carpetAreas: [],
		plotAreas: [],
		servantRooms: [],
		utilities: [],
		levels: [],
		blocks: [],
		configIds: [],
		constructedOns: [],
		createdAt: new Date(),
		desperate: false,
		documents: [],
		exclusive: false,
		floors: [],
		gardens: [],
		greatPriced: false,
		halls: [subUnit.halls],
		identifier: "",
		incompleteProcesses: [],
		kitchens: [subUnit.kitchens],
		landParcel: 0,
		lastCallAt: new Date(),
		lastRemarks: "",
		locality: "",
		location: undefined,
		lpd: {},
		marketing: { status: false },
		mediaListUpdatedAt: new Date(),
		nonNegotiable: false,
		numOfUnits: 0,
		openSpaceArea: "",
		optimization: {},
		penthouse: false,
		plotFacings: [],
		price: "",
		isCommunity: "",
		propertyTypeObj: undefined,
		rentalValues: [subUnit.rentalValue],
		reraStatus: "",
		saleStatus: "",
		sellerProjectId: "",
		slug: "",
		totalBasements: 0,
		totalBlocks: 0,
		totalFloors: 0,
		totalUnits: 0,
		unitId: subUnit.unitId,
		unitNames: [],
		units: [],
		urgent: false,
		villaTypes: [],
		waterSource: undefined,
		removed: false,
		additionalFeatures: [],
		address: "",
		availabilityTypes: [],
		configurations: [],
		bankApprovals: [],
		constructedOnsFromProject: [],
		documentsFromProject: [],
		approvals: [],
		subUnits: [],
		loan: undefined,
		monthlyMaintenanceCosts: [],
		spaceSummary: undefined,
	};
};

// Helper function to get floor display text
const getFloorDisplayText = (floor: number, unitIndex: number) => {
	let floorName = "";
	if (floor === 0) floorName = "Ground Floor";
	else if (floor === 1) floorName = "First Floor";
	else if (floor === 2) floorName = "Second Floor";
	else if (floor === 3) floorName = "Third Floor";
	else floorName = `${floor}th Floor`;

	return `${floorName} Unit - ${unitIndex + 1}`;
};

export const SubUnitsSection: React.FC<SubUnitsSectionProps> = ({ subUnits }) => {
	if (!subUnits || subUnits.length === 0) {
		return null;
	}

	const groupedSubUnits = groupSubUnitsByFloor(subUnits);

	return (
		<div>
			{/* Carousel section */}
			<div className="mt-3 relative flex flex-wrap gap-y-4">
				{Object.entries(groupedSubUnits)
					.sort(([_1, u1], [_2, u2]) => u1.length - u2.length)
					.map(([floor, units]) => {
						const unitsInRow = units.length;

						return units.map((unit, index) => (
							<div key={`${floor}-${index}`} className="basis-full @xl:basis-1/2 @4xl:basis-1/3 overflow-hidden">
								{/* Floor subtitle with extended underline */}
								<div className="relative mb-2">
									<div className="pl-2 text-title-regular text-primary-900 mb-1 break-words leading-tight min-h-[1.5rem] flex items-center">
										{getFloorDisplayText(parseInt(floor), index)}
									</div>
									{/* Extended underline that stretches to the last card in the row */}
									<div
										className="h-0.5 bg-primary-200"
										style={{
											width: index === unitsInRow - 1 ? "100%" : "calc(100% + 1rem)", // Extend beyond card boundary
											marginRight: index === unitsInRow - 1 ? "0" : "-1rem", // Negative margin to extend
										}}
									/>
								</div>
								<div className="cursor-pointer pr-4 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] rounded-2xl w-full">
									<SUPCard data={convertSubUnitToCardData(unit)} className="h-full" shouldRedirect={false} subUnit />
								</div>
							</div>
						));
					})}
			</div>
		</div>
	);
};
